import { useEffect, useState } from "react";
import { Loader2, Send, CreditCard, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";

const ta = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40";

/**
 * Inner form: DialogContent only mounts while open, so this remounts fresh every
 * time the dialog opens — no reset effect needed.
 */
function LawPayForm({ invoice, onClose, onDone }) {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);   // { default_stage, stages[], recipient_email }
  const [stage, setStage] = useState("");
  const [email, setEmail] = useState(invoice?.client_email || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/lawpay/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, operating_account_set: false }));
    // Resolve all five stages for this invoice and default to the age-appropriate one.
    api.get(`/invoices/${invoice.id}/lawpay/preview`)
      .then((r) => {
        const p = r.data;
        setPreview(p);
        const def = p.default_stage || "initial";
        setStage(def);
        const t = (p.stages || []).find((s) => s.key === def);
        if (t) { setSubject(t.subject); setBody(t.body); }
        if (p.recipient_email && !email) setEmail(p.recipient_email);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching the template stage pre-fills resolved subject + message (signature
  // appended). Stays fully editable afterward.
  const pickStage = (key) => {
    setStage(key);
    const t = (preview?.stages || []).find((s) => s.key === key);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/invoices/${invoice.id}/lawpay/request`, {
        recipient_email: email.trim() || undefined,
        subject: subject.trim() || undefined,
        body: body.trim() || undefined,
      });
      setResult(r.data);
      onDone?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const blocked = status && (!status.configured || !status.operating_account_set);

  if (result) {
    return (
      <div className="space-y-3 py-2" data-testid="lawpay-request-success">
        <div className="flex items-start gap-2 rounded-md border border-status-paid/30 bg-status-paid/10 px-3 py-2.5 text-sm text-foreground">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-status-paid" />
          <div>
            <p className="font-medium">Payment request sent to {result.sent_to}</p>
            <p className="text-xs text-muted-foreground">Requested {money(result.amount_requested)} · LawPay request {result.lawpay_request_id}</p>
          </div>
        </div>
        {result.payment_url && (
          <a href={result.payment_url} target="_blank" rel="noreferrer" data-testid="lawpay-payment-url" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <ExternalLink className="h-4 w-4" /> View payment page
          </a>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="lawpay-done-button">Done</Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-1">
      {blocked && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-foreground" data-testid="lawpay-not-configured">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div>
            {!status.send_app_configured && <p>LawPay invoicing isn&apos;t configured — add the LawPay OAuth app credentials to the backend environment.</p>}
            {status.send_app_configured && status.send_expired && <p>LawPay connection expired — reconnect the firm&apos;s account in Settings → LawPay payments.</p>}
            {status.send_app_configured && !status.send_expired && !status.send_connected && <p>Connect the firm&apos;s LawPay account in Settings → LawPay payments first.</p>}
            {status.send_connected && !status.operating_account_set && <p>Set the LawPay operating account in Settings before sending requests.</p>}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="lawpay-stage">Message template</Label>
        <Select id="lawpay-stage" data-testid="lawpay-stage-select" value={stage} onChange={(e) => pickStage(e.target.value)} disabled={!preview}>
          {(preview?.stages || []).map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </Select>
        {preview && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="lawpay-stage-hint">
            Defaulted to <span className="font-medium text-foreground">{(preview.stages.find((s) => s.key === preview.default_stage) || {}).label}</span> for this invoice&apos;s age ({preview.age_days} days since issue). Edit anything before sending.
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="lawpay-email">Recipient email</Label>
        <Input id="lawpay-email" data-testid="lawpay-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" />
      </div>
      <div>
        <Label htmlFor="lawpay-subject">Subject</Label>
        <Input id="lawpay-subject" data-testid="lawpay-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Invoice ${invoice?.number} from your attorney`} />
      </div>
      <div>
        <Label htmlFor="lawpay-body">Message</Label>
        <textarea id="lawpay-body" data-testid="lawpay-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={10} className={ta} />
      </div>

      <p className="text-xs text-muted-foreground" data-testid="lawpay-refund-note">
        Heads up: voiding a LawPay payment in MatterTab is a record reversal only — it does not refund the client. Refunds are issued manually in LawPay.
      </p>

      {error && <p className="text-sm text-destructive" data-testid="lawpay-request-error">{error}</p>}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="lawpay-cancel-button">Cancel</Button>
        <Button onClick={send} disabled={busy || blocked} data-testid="lawpay-send-button"
          className="bg-send text-send-foreground hover:bg-send/90 focus-visible:ring-send/40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
        </Button>
      </DialogFooter>
    </div>
  );
}

export function LawPayRequestDialog({ open, onOpenChange, invoice, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="lawpay-request-dialog">
        <DialogTitle className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary"><CreditCard className="h-4 w-4" /></span>
          Request payment via LawPay
        </DialogTitle>
        <DialogDescription>
          Email the client a secure LawPay payment request with invoice {invoice?.number} attached. When they pay, MatterTab records the payment automatically.
        </DialogDescription>
        <LawPayForm invoice={invoice} onClose={() => onOpenChange(false)} onDone={onDone} />
      </DialogContent>
    </Dialog>
  );
}
